import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuthContext } from "@trillion/auth";
import { Alert, AlertDescription } from "@trillion/ui/components/ui/alert";
import { Button } from "@trillion/ui/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@trillion/ui/components/ui/card";
import { ApiError } from "@trillion/ui/lib/api-client";
import { getEnv } from "@trillion/ui/lib/env";
import { mcpConsentApi, type McpConsentDecision, type McpConsentView } from "../lib/mcp-consent-api";
import { isPassportAuthorizeUrl } from "../lib/login-return-url";

export function OAuthConsentPage() {
  const { user, isLoading } = useAuthContext();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("requestId");
  const [view, setView] = useState<McpConsentView | null>(null);
  const [decision, setDecision] = useState<McpConsentDecision | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const busy = useRef(false);
  const actor = useRef<string | null>(null);
  const continueButton = useRef<HTMLButtonElement>(null);
  const actorId = user?.id ?? null;

  useEffect(() => {
    if (isLoading) return;
    if (!actorId || (actor.current && actor.current !== actorId)) {
      setView(null);
      setDecision(null);
      setError("Your session changed or ended. Restart this connection from your coding client.");
      return;
    }
    actor.current = actorId;
    if (!requestId) {
      setError("This connection request is invalid or expired. Restart it from your coding client.");
      return;
    }
    const controller = new AbortController();
    setView(null);
    setDecision(null);
    setError(null);
    mcpConsentApi.get(requestId, controller.signal).then(value => {
      if (!controller.signal.aborted) setView(value);
    }).catch(reason => {
      if (controller.signal.aborted) return;
      setError(reason instanceof ApiError && reason.status === 401
        ? "Your session ended. Restart this connection from your coding client."
        : "This connection request is unavailable, invalid or expired. Restart it from your coding client.");
    });
    return () => controller.abort();
  }, [actorId, isLoading, requestId]);

  useEffect(() => {
    if (decision) continueButton.current?.focus();
  }, [decision]);

  async function decide(approve: boolean) {
    if (!view || busy.current || actor.current !== actorId) return;
    busy.current = true;
    setSubmitting(true);
    try {
      const result = await mcpConsentApi.decide(view.request_id, approve, view.csrf_token);
      if (!isPassportAuthorizeUrl(result.continue_url, getEnv("PASSPORT_API_URL")))
        throw new Error("Untrusted continuation");
      setDecision(result);
    } catch {
      setError("The request could not be completed. It may have expired or already been used. Restart this connection from your coding client.");
    } finally {
      busy.current = false;
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-xl">
        <CardHeader>
          <CardTitle>Connect to Board</CardTitle>
          <CardDescription>Choose whether this coding client can use Board with your account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
            : isLoading || !view ? <p role="status">Loading connection request…</p>
            : decision ? <div role="status"><p>{decision.approved ? "Connection approved" : "Connection denied"}.</p><p>Connection {decision.connection_id}</p><p>Continue to return the result to your coding client.</p></div>
            : <>
              <dl className="space-y-3 break-words">
                <div><dt className="text-sm text-muted-foreground">Acting account</dt><dd>{view.account.display_name}</dd><dd>{view.account.email}</dd></div>
                <div><dt className="text-sm text-muted-foreground">Client</dt><dd>{view.client.display_name ?? view.client.client_id}</dd><dd className="font-mono text-sm">{view.client.client_id}</dd></div>
                <div><dt className="text-sm text-muted-foreground">Connection</dt><dd>{view.connection_id}</dd></div>
                <div><dt className="text-sm text-muted-foreground">Board resource</dt><dd>{view.resource}</dd></div>
              </dl>
              <p>This client can read, edit, comment, share and restore Board diagrams within your existing Board permissions.</p>
              {view.offline_access && <p>Offline access: this connection can renew access while you are away, until you disconnect it.</p>}
              <p className="text-sm text-muted-foreground">The public client ID is configuration, not proof of the client vendor’s identity.</p>
            </>}
        </CardContent>
        <CardFooter className="flex flex-wrap gap-3">
          {error ? <Button asChild variant="outline"><Link to="/login">Sign in and restart connection</Link></Button>
            : decision ? <Button ref={continueButton} onClick={() => {
              if (isPassportAuthorizeUrl(decision.continue_url, getEnv("PASSPORT_API_URL"))) window.location.assign(decision.continue_url);
            }}>Continue</Button>
            : view && !isLoading && <>
              <Button disabled={submitting} onClick={() => void decide(true)}>{submitting ? "Saving…" : "Approve"}</Button>
              <Button variant="outline" disabled={submitting} onClick={() => void decide(false)}>Deny</Button>
            </>}
        </CardFooter>
      </Card>
    </main>
  );
}
